import * as React from 'react';
import { useState } from 'react';
import { connect } from 'react-redux';
import queryString from 'query-string';

import * as Edge from '../../core';
import { AppState } from '../../store';
import errorLoadingWrapperHOC from '../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import { RouteComponentProps, generatePath } from 'react-router';
import { withLoadDataDefaultConfig } from '../../components/loadData';
import { CommerceService } from '../../services/commerceService';
import { CheckoutPurchaseModel } from './models';
import ChooseProduct from './chooseProduct';
import { getPrices } from '../../store/staticData';
import { ChooseProductLayout, GlassesOptionLayout } from '../../layouts/greyScreenLayout';
import ScrollToTopOnMount from '../../components/global/scrollToTopOnMount';
import _ from 'lodash';
import { SessionService } from '../../services/sessionService';
import GlassesOption from './GlassesOption';
import { EdgeError } from '../../core/models';
import Error from '../../components/global/error';
import Loading from '../../components/loading';

export interface CheckoutPageOptions {
	[key: string]: string;
}

export interface CheckoutSession {
	cancelUrl: string;
	checkoutSessionType: Edge.Models.CheckoutSessionType;
	country?: string;
	email: string;
	entityId: string;
	entityType: Edge.Models.EntityType;
	glassesPrice?: string;
	glassesQuantity?: number;
	mode: string;
	name: string;
	organizationId: string;
	price: string;
	quantity: number;
	sport: string;
	successUrl: string;
}

interface CheckoutPageParams {
	checkoutPageStage?: CheckoutPageStage;
	checkoutSessionType: Edge.Models.CheckoutSessionType;
	entityId: string;
	entityType: Edge.Models.EntityType;
}

export enum CheckoutPageStage {
	ChooseProduct = '0',
	GlassesOption = '1',
}

export interface CheckoutPageProps extends RouteComponentProps<CheckoutPageParams> {
	addSeatAmount?: number;
	addSeatPrice?: Edge.Models.OrganizationAllowedPrice;
	email?: string;
	entityType: Edge.Models.EntityType;
	latestPurchasePlan?: Edge.Models.LatestPurchasePlanResponse;
	name: string;
	organizationId: string;
	prices: Edge.Models.OrganizationAllowedPrice[];
	sport?: string;
}

export const generatePurchasePageLink = (
	entityType: Edge.Models.EntityType,
	entityId: string,
	checkoutSessionType: Edge.Models.CheckoutSessionType,
	checkoutPageStage?: CheckoutPageStage,
	options: CheckoutPageOptions = {}
) => {
	const query = queryString.stringify(options);
	return (
		generatePath('/checkout/:entityType/:entityId/:checkoutSessionType/:checkoutPageStage?', {
			entityType,
			entityId,
			checkoutSessionType,
			checkoutPageStage,
		}) +
		'?' +
		query
	);
};

/**
 * Usage scenarios:
 * 1. Extend/upgrade existing subscription (orderType: Extend)
 * 2. Add seats to existing subscription (orderType: AddSeats)
 * 3. Add sessions/subscription (cannot have an active subscription) (orderType: Initial)
 *
 * Scope:
 * - Can operate at the organization or user level (specified by route parameters)
 */

export function CheckoutPage({
	addSeatPrice,
	email,
	latestPurchasePlan,
	match,
	name,
	organizationId,
	prices: allPrices,
	sport,
}: CheckoutPageProps) {
	const { checkoutPageStage, checkoutSessionType, entityId, entityType } = match.params;
	const [stage, setStage] = useState<CheckoutPageStage>(checkoutPageStage || CheckoutPageStage.ChooseProduct);
	const [checkout, setCheckout] = useState<CheckoutSession | undefined>(undefined);
	const [error, setError] = useState<EdgeError>();

	const addSeats = checkoutSessionType === Edge.Models.CheckoutSessionType.AddSeats;
	const extend = checkoutSessionType === Edge.Models.CheckoutSessionType.Extend;

	const glassPrice = React.useMemo(
		() => allPrices.find((p) => p.price.priceType === Edge.Models.PriceTypes.Glasses),
		[allPrices]
	);
	const [model, setModel] = useState<CheckoutPurchaseModel>({
		price: checkoutSessionType === Edge.Models.CheckoutSessionType.AddSeats ? addSeatPrice : undefined,
		numberOfGlasses: 0,
		quantity:
			(extend
				? latestPurchasePlan && latestPurchasePlan.subscription && latestPurchasePlan.subscription.seats
				: undefined) || 1,
		overrideProductPrice: addSeats ? latestPurchasePlan && latestPurchasePlan.addSeatPrice : undefined,
	});

	const cancelUrl = `${origin}/${
		entityType === Edge.Models.EntityType.User ? 'my-account' : 'manage-organization'
	}/manage-plan`;
	const successUrl = `${origin}/checkout-successful`;

	React.useEffect(() => {
		setModel((previousValue: CheckoutPurchaseModel) =>
			glassPrice && checkoutPageStage && checkoutPageStage === CheckoutPageStage.GlassesOption
				? {
						...model,
						price: glassPrice,
						coupon: (glassPrice || {}).discount,
				  }
				: previousValue
		);
		setCheckout((previousValue: CheckoutSession | undefined) =>
			glassPrice && checkoutPageStage && checkoutPageStage === CheckoutPageStage.GlassesOption
				? {
						cancelUrl,
						checkoutSessionType,
						email: email || 'N/A',
						entityId,
						entityType,
						glassesPrice: glassPrice.price.id,
						glassesQuantity: 1,
						mode: 'payment',
						name: name || 'N/A',
						organizationId,
						price: glassPrice.price.id,
						quantity: 1,
						sport: sport || 'None',
						successUrl,
				  }
				: previousValue
		);
	}, [
		cancelUrl,
		checkoutPageStage,
		checkoutSessionType,
		email,
		entityId,
		entityType,
		glassPrice,
		model,
		name,
		organizationId,
		sport,
		successUrl,
	]);

	const onSelectPrice = async (price: Edge.Models.OrganizationAllowedPrice) => {
		setModel({ ...model, price, coupon: price.discount });

		const checkout: CheckoutSession = {
			cancelUrl,
			checkoutSessionType,
			email: email || 'N/A',
			entityId,
			entityType,
			glassesPrice: glassPrice && glassPrice.price.id,
			glassesQuantity: 1,
			mode: price.price.priceType === Edge.Models.PriceTypes.Recurring ? 'subscription' : 'payment',
			name: name || 'N/A',
			organizationId,
			price: price.price.id,
			quantity: 1,
			sport: sport || 'None',
			successUrl,
		};

		setCheckout(checkout);
		setStage(CheckoutPageStage.GlassesOption);
	};

	const prices =
		checkoutSessionType === Edge.Models.CheckoutSessionType.AddSeats
			? [addSeatPrice!]
			: checkoutSessionType === Edge.Models.CheckoutSessionType.Extend
			? allPrices.filter(
					(i: Edge.Models.OrganizationAllowedPrice) => i.price.priceType !== Edge.Models.PriceTypes.Sessions
			  )
			: allPrices;

	switch (stage) {
		case CheckoutPageStage.GlassesOption:
			return checkout ? (
				<GlassesOptionLayout>
					<GlassesOption
						checkout={checkout}
						isRecurring={!!model.price && model.price.price.priceType === Edge.Models.PriceTypes.Recurring}
						updateGlassesQuantity={(glassesQuantity: number) => {
							setCheckout((previous: CheckoutSession | undefined) => ({
								...previous!, // can't be undefined given the ternary above
								glassesQuantity,
							}));
						}}
						setError={setError}
						{...(!!model.price && model.price.price.priceType === Edge.Models.PriceTypes.Recurring
							? {
									productRenewalMonths: model.price.price.months,
							  }
							: {})}
					/>
					{error && (
						<Error>
							{Edge.API.getStatus(error) === 500
								? 'Cannot fulfill request'
								: Edge.API.getErrorMessage(error)}
						</Error>
					)}
				</GlassesOptionLayout>
			) : (
				<Loading />
			);
		default:
			return (
				<ChooseProductLayout>
					<ScrollToTopOnMount />
					<ChooseProduct
						prices={prices}
						count={
							checkoutSessionType === Edge.Models.CheckoutSessionType.Extend ? model.quantity : undefined
						}
						onSelectPrice={onSelectPrice}
					/>
				</ChooseProductLayout>
			);
	}
}

function mapStateToProps({ loginState, organizationState, priceState, teamState }: AppState) {
	return {
		allPrices: priceState.data,
		allPricesIsLoading: priceState.isLoading,
		email: loginState.decodedToken && loginState.decodedToken.email,
		name: loginState.decodedToken && `${loginState.decodedToken.given_name} ${loginState.decodedToken.family_name}`,
		organizationId: (organizationState.currentOrganization && organizationState.currentOrganization.id)!,
		sport:
			(teamState.currentTeam && teamState.currentTeam.sport.name) ||
			(teamState.teams && teamState.teams.length > 0 && teamState.teams[0].sport.name) ||
			undefined,
	};
}

interface MappedProps extends CheckoutPageProps {
	allPricesIsLoading?: boolean;
	allPrices?: Edge.Models.Price[];
	getPrices: () => void;
}

export default connect(
	mapStateToProps,
	{ getPrices: getPrices }
)(
	withLoadDataDefaultConfig(
		errorLoadingWrapperHOC<MappedProps>(
			CheckoutPage as React.FunctionComponent<MappedProps>,
			(props) => !!props.organizationId && !!props.allPrices,
			undefined,
			(props) => {
				if (!props.allPrices && !props.allPricesIsLoading) {
					props.getPrices();
				}
			}
		),
		(props) => {
			return {
				..._.pick(props.match.params, 'entityType', 'entityId', 'checkoutSessionType'),
				name: props.name,
				organizationId: props.organizationId,
				sport: props.sport,
			};
		},
		async (props) => {
			const [latestPurchasePlan, prices] = await Promise.all([
				SessionService.getLatestPurchasePlan(props.entityType!, props.entityId!, props.organizationId),
				CommerceService.getOrganizationAllowedPrices(props.organizationId, props.entityType!, props.entityId!),
			]);

			return {
				addSeatPrice: prices.filter((i) => latestPurchasePlan && i.price.id === latestPurchasePlan.price.id)[0],
				latestPurchasePlan,
				name: props.name,
				prices,
				sport: props.sport,
			};
		}
	)
);
